// Task 175 D7 — data-source hook for the signed imgproxy URL of a
// single screenshot.
//
// Mirrors the `useDashboardData.ts` shape: switches on `mockupMode`
// from `AppContext`. In mockup mode it returns a placeholder data: URL
// (no network); in live mode it calls
// `GET /api/v1/orgs/:orgId/screenshots/:screenshotId?width=…` and
// returns the signed `url` from the response body.
//
// The route returns `{ url, expiresAt }`. The hook surfaces only `url`
// + `isLoading` for now; if a future caller needs to refresh on
// expiry, lift `expiresAt` out at that point. React Query's default
// staleTime + refetchOnWindowFocus already covers most of the
// "refresh stale URLs" case.

import { useQuery } from '@tanstack/react-query';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';

interface ScreenshotUrlResponse {
  url: string;
  expiresAt: string;
}

interface UseScreenshotUrlArgs {
  screenshotId: string;
  /** When omitted, the request URL has no `?width=` query param and
   *  imgproxy serves the source at its largest delivered variant.
   *  The query key still includes `args.width`, so the cache slot is
   *  distinct from any sized request. (Task 180.) */
  width?: number;
  height?: number;
  format?: 'webp' | 'avif' | 'jpeg' | 'png';
}

interface UseScreenshotUrlResult {
  url: string | null;
  isLoading: boolean;
}

// 1×1 transparent PNG. Used as the mockup-mode source so layout, lazy-
// loading, and tests can exercise the component without contacting any
// real image surface. Browsers render this as a transparent pixel.
const MOCKUP_PLACEHOLDER_DATA_URL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

export function useScreenshotUrl(args: UseScreenshotUrlArgs): UseScreenshotUrlResult {
  const { mockupMode, currentOrg } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  const query = useQuery({
    queryKey: ['screenshotUrl', orgId, args.screenshotId, args.width, args.height, args.format],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      if (args.width !== undefined) qs.set('width', String(args.width));
      if (args.height !== undefined) qs.set('height', String(args.height));
      if (args.format !== undefined) qs.set('format', args.format);
      const queryString = qs.toString();
      const path = queryString
        ? `/api/v1/orgs/${orgId}/screenshots/${args.screenshotId}?${queryString}`
        : `/api/v1/orgs/${orgId}/screenshots/${args.screenshotId}`;
      const res = await apiFetch<ScreenshotUrlResponse>(path, token);
      return res;
    },
    // Match the IMGPROXY_TTL window — re-fetching before the URL
    // expires is wasted, and re-fetching after produces a fresh URL.
    // The 50-minute stale time is generous; the route's actual
    // expiresAt is the source of truth if a future caller needs to
    // refresh more aggressively.
    staleTime: 50 * 60 * 1000,
    enabled: !mockupMode && !!orgId && !!args.screenshotId,
  });

  if (mockupMode) {
    return { url: MOCKUP_PLACEHOLDER_DATA_URL, isLoading: false };
  }
  return {
    url: query.data?.url ?? null,
    isLoading: query.isLoading,
  };
}
